import { useAsync, useMap, useMountEffect, useUpdateEffect } from "@react-hookz/web";
import axios from "axios";
import { differenceInMinutes } from "date-fns";
import difference from "lodash/difference";
import uniqBy from "lodash/uniqBy";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { fetchQuery, graphql, readInlineData, useRelayEnvironment } from "react-relay";
import { createOperationDescriptor, type Disposable, getRequest } from "relay-runtime";
import { scmpPlus, useSdkContext } from "scmp-sdk/components/contexts";

import { useFallbackStorageValue } from "shared/hooks";
import { notEmpty } from "shared/lib/utils";

import type { hooksRemoveReadingHistoryArticle$key } from "scmp-app/queries/__generated__/hooksRemoveReadingHistoryArticle.graphql";
import type {
  hooksUseReadingHistoryWithArticleQuery,
  hooksUseReadingHistoryWithArticleQuery$data,
  hooksUseReadingHistoryWithArticleQuery$variables,
} from "scmp-app/queries/__generated__/hooksUseReadingHistoryWithArticleQuery.graphql";

import { create, fetch, removeAllAtDate, removeByUuidAtDate } from "./apis";
import { LocalStorageKeys, ReadingHistoryItemSize } from "./consts";
import { formatGroupDate } from "./helpers";

export const useReadingHistorySetup = () => {
  const { currentArticleUuid, user } = useSdkContext();

  const {
    createReadingHistory,
    fetchReadingHistory,
    lastSyncedDate,
    resetLocalReadingHistory,
    syncReadingHistory,
  } = useReadingHistoryOperations();

  // Fetch remote data if not yet fetched
  useMountEffect(async () => {
    await fetchReadingHistory();
  });

  // Clear local storage for anonymous user
  useEffect(() => {
    if (user) return;
    resetLocalReadingHistory();
  }, [resetLocalReadingHistory, user]);

  // Create reading history for current article
  useEffect(() => {
    if (!currentArticleUuid) return;
    createReadingHistory(currentArticleUuid);
  }, [createReadingHistory, currentArticleUuid]);

  // Sync pending reading history periodically
  useUpdateEffect(() => {
    if (differenceInMinutes(new Date(), new Date(lastSyncedDate ?? 0)) < 1) return;
    void syncReadingHistory();
  }, [syncReadingHistory, currentArticleUuid, lastSyncedDate]);
};

export const useReadingHistory = () => {
  const { user } = useSdkContext();

  const { pendingReadingHistory, syncedReadingHistory } = useReadingHistoryLocalStorage();

  const readingHistory = useMemo(() => {
    if (!user) return [];

    const merged = [...(pendingReadingHistory ?? []), ...(syncedReadingHistory ?? [])].sort(
      (a, b) => b.createdAt - a.createdAt,
    );
    return merged;
  }, [pendingReadingHistory, syncedReadingHistory, user]);

  return {
    readingHistory,
  };
};

export const useReadingHistoryOperations = () => {
  const { config, handleTrackGA4, user } = useSdkContext();

  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: config.readingHistory.host,
      headers: { apikey: config.readingHistory.apiKey },
    });
    return instance;
  }, [config]);

  const {
    lastSyncedDate,
    pendingReadingHistory,
    setLastSyncedDate,
    setPendingReadingHistory,
    setSyncedReadingHistory,
  } = useReadingHistoryLocalStorage();

  const fetchReadingHistory = useCallback(async () => {
    if (!user || lastSyncedDate) return;

    const remoteData = await fetch(axiosInstance, user.uuid, ReadingHistoryItemSize);
    setSyncedReadingHistory(
      remoteData.map(item => ({
        createdAt: new Date(item.createdAt).getTime(),
        entityUuid: item.entityUuid,
      })),
    );
    setLastSyncedDate(Date.now());
  }, [axiosInstance, lastSyncedDate, setLastSyncedDate, setSyncedReadingHistory, user]);

  const syncReadingHistory = useCallback(async () => {
    if (!user) return;
    if (!pendingReadingHistory || pendingReadingHistory.length === 0) return;

    const pendingItems = pendingReadingHistory.map(item => ({
      createdAt: new Date(item.createdAt),
      entityUuid: item.entityUuid,
      expiresAt: new Date(item.createdAt),
    }));
    await create(axiosInstance, user.uuid, pendingItems);

    try {
      const remoteData = await fetch(axiosInstance, user.uuid, ReadingHistoryItemSize);
      setSyncedReadingHistory(
        remoteData.map(item => ({
          createdAt: new Date(item.createdAt).getTime(),
          entityUuid: item.entityUuid,
        })),
      );
      setLastSyncedDate(Date.now());
    } catch {
      setSyncedReadingHistory(history => [...(pendingReadingHistory ?? []), ...(history ?? [])]);
    } finally {
      setPendingReadingHistory([]);
    }
  }, [
    axiosInstance,
    pendingReadingHistory,
    setLastSyncedDate,
    setPendingReadingHistory,
    setSyncedReadingHistory,
    user,
  ]);

  const createReadingHistory = useCallback(
    (entityUuid: string) => {
      if (!user) return;

      const newItem = { createdAt: Date.now(), entityUuid };

      setPendingReadingHistory(history => {
        const data = uniqBy(
          [newItem, ...(history ?? [])],
          item => `${item.entityUuid}-${formatGroupDate(item.createdAt)}`,
        );
        return data;
      });
      setSyncedReadingHistory(history =>
        [...(history ?? [])].filter(
          item =>
            !(
              item.entityUuid === newItem.entityUuid &&
              formatGroupDate(item.createdAt) === formatGroupDate(newItem.createdAt)
            ),
        ),
      );
    },
    [setPendingReadingHistory, setSyncedReadingHistory, user],
  );

  const clearReadingHistory = useCallback(
    async (groupDate: string) => {
      if (!user) return;

      const filter: Parameters<typeof setPendingReadingHistory>[0] = history =>
        [...(history ?? [])].filter(item => formatGroupDate(item.createdAt) !== groupDate);
      setPendingReadingHistory(filter);
      setSyncedReadingHistory(filter);
      await removeAllAtDate(axiosInstance, groupDate, user.uuid);

      handleTrackGA4({
        action: "click",
        category: "readinghistory",
        customized_parameters: {
          action_type: "clear",
        },
        subcategory: "",
      });
    },
    [axiosInstance, handleTrackGA4, setPendingReadingHistory, setSyncedReadingHistory, user],
  );

  const removeReadingHistory = useCallback(
    async (reference: hooksRemoveReadingHistoryArticle$key, createdAt: number) => {
      if (!user) return;

      const { entityId, entityUuid } = readInlineData(
        graphql`
          fragment hooksRemoveReadingHistoryArticle on Article @inline {
            entityUuid
            entityId
          }
        `,
        reference,
      );

      const filter: Parameters<typeof setPendingReadingHistory>[0] = history =>
        [...(history ?? [])].filter(
          item => !(item.entityUuid === entityUuid && item.createdAt === createdAt),
        );
      setPendingReadingHistory(filter);
      setSyncedReadingHistory(filter);
      await removeByUuidAtDate(axiosInstance, entityUuid, formatGroupDate(createdAt));

      handleTrackGA4({
        action: "click",
        category: "readinghistory",
        customized_parameters: {
          action_type: "remove",
          article_id: entityId ?? "",
        },
        subcategory: "",
      });
    },
    [axiosInstance, handleTrackGA4, setPendingReadingHistory, setSyncedReadingHistory, user],
  );

  const resetLocalReadingHistory = useCallback(() => {
    setPendingReadingHistory([]);
    setSyncedReadingHistory([]);
    setLastSyncedDate(0);
  }, [setLastSyncedDate, setPendingReadingHistory, setSyncedReadingHistory]);

  return {
    clearReadingHistory,
    createReadingHistory,
    fetchReadingHistory,
    lastSyncedDate,
    removeReadingHistory,
    resetLocalReadingHistory,
    syncReadingHistory,
  };
};

const useReadingHistoryLocalStorage = () => {
  type StoredReadingHistory = {
    createdAt: number;
    entityUuid: string;
  };

  const { set: setPendingReadingHistory, value: pendingReadingHistory } = useFallbackStorageValue<
    StoredReadingHistory[]
  >(LocalStorageKeys.pending);
  const { set: setSyncedReadingHistory, value: syncedReadingHistory } = useFallbackStorageValue<
    StoredReadingHistory[]
  >(LocalStorageKeys.synced);
  const { set: setLastSyncedDate, value: lastSyncedDate } = useFallbackStorageValue<number>(
    LocalStorageKeys.lastSyncedDate,
  );

  return {
    lastSyncedDate,
    pendingReadingHistory,
    setLastSyncedDate,
    setPendingReadingHistory,
    setSyncedReadingHistory,
    syncedReadingHistory,
  };
};

export const useReadingHistoryWithArticle = () => {
  const environment = useRelayEnvironment();
  const { readingHistory } = useReadingHistory();
  const articleNodeMap = useMap<string, ArticleNode>();
  const disposableReference = useRef<Disposable>();

  const [state, actions] = useAsync(async () => {
    const newReadingHistoryEntityUuids = difference(
      readingHistory.map(item => item.entityUuid),
      [...articleNodeMap.keys()],
    );

    if (newReadingHistoryEntityUuids.length > 0) {
      const query = graphql`
        query hooksUseReadingHistoryWithArticleQuery(
          $entityUuids: [String]!
          $first: Int!
          $scmpPlusPaywallTypeIds: [String]
        ) {
          articles(
            exclude: { paywallTypeIds: $scmpPlusPaywallTypeIds }
            filter: { entityUuids: $entityUuids }
            first: $first
          ) {
            edges {
              node {
                entityUuid
                entityId
                ...readingHistoryPanelArticle
                ...readingHistoryActionMenuBookmarkButtonArticle
                ...readingHistoryActionMenuSocialButtonArticle
                ...hooksRemoveReadingHistoryArticle
              }
            }
          }
        }
      `;
      const variables: hooksUseReadingHistoryWithArticleQuery$variables = {
        entityUuids: [...newReadingHistoryEntityUuids],
        first: newReadingHistoryEntityUuids.length,
        scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
      };

      const queryRequest = getRequest(query);
      const queryDescriptor = createOperationDescriptor(queryRequest, variables);

      const data = await fetchQuery<hooksUseReadingHistoryWithArticleQuery>(
        environment,
        query,
        variables,
      ).toPromise();

      disposableReference.current = environment.retain(queryDescriptor);
      data?.articles.edges.forEach(({ node }) => {
        articleNodeMap.set(node.entityUuid, node);
      });
    }

    const readingHistoryWithArticle = readingHistory
      .map(item => {
        const matched = articleNodeMap.get(item.entityUuid);
        if (!matched) return;
        return { ...item, article: matched };
      })
      .filter(notEmpty);

    return readingHistoryWithArticle;
  }, []);

  // Trigger when reading history updated
  useEffect(() => {
    void actions.execute();
    return () => {
      disposableReference.current?.dispose();
    };
  }, [actions, readingHistory]);

  return { state };

  type ArticleNode =
    hooksUseReadingHistoryWithArticleQuery$data["articles"]["edges"][number]["node"];
};
export type ReadingHistoryWithArticle = ReturnType<
  typeof useReadingHistoryWithArticle
>["state"]["result"][number];

export type ReadingHistoryWithArticleState = ReturnType<
  typeof useReadingHistoryWithArticle
>["state"];
